import { Button } from "@/components/ui/button";
import { MarkdownEditor } from "@/components/ui/markdown-editor";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { useDraft } from "@/lib/use-draft";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";

/**
 * `/projects/:slug/wiki/new` — create a wiki page. Pushed to GitLab's
 * wiki first (GitLab confirms, then Hub mirrors), same order as the
 * write-through on work items.
 */

export const Route = createFileRoute("/_app/projects/$slug/wiki/new")({
  component: WikiNewPage,
});

function WikiNewPage() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useDraft(`wiki-new:${slug}:title`);
  const [content, setContent] = useDraft(`wiki-new:${slug}:content`);
  const create = useApiMutation(
    (body: { title: string; content: string }) =>
      api.post<{ slug: string; title: string }>(`/api/projects/${slug}/wiki`, body),
    {
      onSuccess: (page) => {
        setTitle("");
        setContent("");
        return navigate({
          to: "/projects/$slug/wiki/$wikiSlug",
          params: { slug, wikiSlug: page.slug },
        });
      },
      errorMessage: (err) =>
        err instanceof ApiError
          ? "Couldn't create the page — you may not have permission."
          : "Couldn't create the page.",
    },
  );

  async function createPage(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    await create.mutate({ title: title.trim(), content });
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-bold">New Wiki Page</h2>
        <Button variant="outline" size="sm" asChild>
          <Link to="/projects/$slug/wiki" params={{ slug }}>
            Cancel
          </Link>
        </Button>
      </div>

      <div className="rounded-md border bg-card p-6">
        <p className="text-xs text-muted-foreground mb-4">
          Stored in Hub — synced to the project's GitLab wiki too. Format: Markdown.
        </p>
        <form onSubmit={createPage} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="title" className="text-sm font-medium leading-none">
              Title <span className="text-destructive">*</span>
            </label>
            <input
              id="title"
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Page title"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          <div className="space-y-2">
            <span className="text-sm font-medium leading-none block">Content</span>
            <MarkdownEditor
              value={content}
              onChange={setContent}
              rows={20}
              placeholder="Write your wiki content here..."
              uploadEndpoint={`/api/projects/${slug}/uploads`}
            />
          </div>

          {create.error && <p className="text-sm text-destructive">{create.error}</p>}

          <div className="flex gap-2">
            <Button type="submit" disabled={create.pending || !title.trim()}>
              {create.pending ? "Creating…" : "Create Page"}
            </Button>
            <Button variant="outline" type="button" asChild>
              <Link to="/projects/$slug/wiki" params={{ slug }}>
                Cancel
              </Link>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
